import { LabelTag } from './LabelTag';
import { BatteryMode, PowerSource } from './tags.types';
import { ResponsiveTooltip } from 'components/labels/ResponsiveTooltip';
import { ReactNode } from 'react';
import { Translation } from 'react-i18next';
import { IconType, ProjectTag } from 'types';
import { Icons } from 'greener-ui-library';

export const POWER_SOURCES: PowerSource[] = ['solar', 'wind', 'hydrogen', 'grid', 'generator', 'batterySwap'];

export const BATTERY_MODES: BatteryMode[] = [
  'peakshave',
  'loadshare',
  'trading',
  'gridBoost',
  'hybrid',
  'droop',
  'afe',
  'standalone',
];

export const POWER_SOURCE_ICON_MAP: Record<PowerSource, IconType> = {
  solar: Icons.SolarPower ,
  wind: Icons.WindPower ,
  hydrogen: Icons.HydrogenGenerator ,
  grid: Icons.Grid ,
  generator: Icons.Generator ,
  batterySwap: Icons.BatterySwap ,
};

export const BATTERY_MODE_LABEL_MAP: Record<BatteryMode, string> = {
  peakshave: 'projects:tags.batteryModes.peakshave',
  loadshare: 'projects:tags.batteryModes.loadshare',
  gridBoost: 'projects:tags.batteryModes.gridBoost',
  hybrid: 'projects:tags.batteryModes.hybrid',
  droop: 'projects:tags.batteryModes.droop',
  afe: 'projects:tags.batteryModes.afe',
  trading: 'projects:tags.batteryModes.trading',
  standalone: 'projects:tags.batteryModes.standalone',
};

export const POWER_SOURCE_LABEL_MAP: Record<PowerSource, string> = {
  solar: 'projects:tags.powerSources.solar',
  wind: 'projects:tags.powerSources.wind',
  hydrogen: 'projects:tags.powerSources.hydrogen',
  grid: 'projects:tags.powerSources.grid',
  generator: 'projects:tags.powerSources.generator',
  batterySwap: 'projects:tags.powerSources.batterySwap',
};

export const getTagValues = (projectTags?: ProjectTag[]) => {
  const powerSourceTags: PowerSource[] = [];
  const batteryModeTags: BatteryMode[] = [];

  projectTags?.forEach((projectTag) => {
    const tagName = projectTag?.name;

    if (POWER_SOURCES.includes(tagName as PowerSource)) {
      powerSourceTags.push(tagName as PowerSource);
    } else if (BATTERY_MODES.includes(tagName as BatteryMode)) {
      batteryModeTags.push(tagName as BatteryMode);
    }
  });

  return { powerSourceTags, batteryModeTags };
};

export const getMatchingTagIds = (projectTags: ProjectTag[], tagNames: string[]): number[] => {
  const matchingTagIds: number[] = [];

  projectTags.forEach((projectTag) => {
    if (tagNames.includes(projectTag.name)) {
      matchingTagIds.push(projectTag.tagId);
    }
  });

  return matchingTagIds;
};

export const generateTagComponents = (projectTags?: ProjectTag[]) => {
  const { powerSourceTags, batteryModeTags } = getTagValues(projectTags);

  const powerSourceTagComponents: ReactNode[] = powerSourceTags.map((tagName) => {
    const IconComponent = POWER_SOURCE_ICON_MAP[tagName];
    return (
      <Translation key={tagName}>
        {(t) => (
          <ResponsiveTooltip
            title={t('projects:tags.tooltip', {
              tagName: t(POWER_SOURCE_LABEL_MAP[tagName]),
            })}
          >
            <IconComponent key={tagName} />
          </ResponsiveTooltip>
        )}
      </Translation>
    );
  });

  const batteryModeTagComponents: ReactNode[] = batteryModeTags.map((tagName) => (
    <LabelTag key={tagName} label={BATTERY_MODE_LABEL_MAP[tagName]} />
  ));

  return { powerSourceTagComponents, batteryModeTagComponents };
};
